export type HelpScope = 'page' | 'field' | 'status' | 'action';
export type HelpAudience = 'all' | 'admin' | 'user';

export interface HelpArticle {
  id: string;
  title: string;
  category: string;
  scope: HelpScope;
  audience?: HelpAudience;
  summary: string;
  body: string[];
  systemBehavior?: string[];
  steps?: string[];
  warnings?: string[];
  bestPractices?: string[];
  relatedIds?: string[];
}

export const DEFAULT_HELP_ARTICLE: HelpArticle = {
  id: 'help.fallback',
  title: 'Aide contextuelle',
  category: 'help',
  scope: 'page',
  audience: 'all',
  summary: "L'aide demandée n'est pas encore documentée.",
  body: [
    "Cette section n'a pas encore d'article dédié.",
    "Utilisez les informations visibles à l'écran et revenez plus tard pour une aide plus précise.",
  ],
};

export const helpRegistry: HelpArticle[] = [
  {
    id: 'outing.create.overview',
    title: 'Configurer une sortie',
    category: 'outing.create',
    scope: 'page',
    audience: 'admin',
    summary: 'Chaque réglage de cette page influence la mise en vente, la réservation client et le suivi admin.',
    body: [
      'Cette page sert à créer une nouvelle sortie ou à corriger une sortie déjà publiée.',
      'Les champs de date, capacité, point de rendez-vous et moyens de paiement ont l’impact métier le plus fort.',
    ],
    steps: [
      'Renseignez les informations de base de la sortie.',
      'Vérifiez le prix, la capacité et le point de rendez-vous.',
      'Activez uniquement les modes de règlement réellement pris en charge.',
    ],
    warnings: [
      'Une mauvaise configuration peut provoquer des réservations incohérentes ou des corrections manuelles côté admin.',
    ],
    relatedIds: ['outing.create.schedule', 'outing.create.capacity', 'outing.create.meeting_point', 'outing.create.accessibility', 'outing.create.payment_setup'],
  },
  {
    id: 'outing.create.payment_setup',
    title: 'Paramétrer les moyens de paiement',
    category: 'outing.create',
    scope: 'field',
    audience: 'admin',
    summary: 'Le choix des moyens de règlement détermine le parcours client, le compte bancaire utilisé et la charge de traitement admin.',
    body: [
      'Carte bancaire simplifie la confirmation immédiate côté client.',
      'Le virement bancaire ajoute un contrôle manuel de la communication, du montant reçu et du compte bancaire de destination.',
      'Si un compte IBAN est renseigné sur la sortie, il prend le dessus sur le compte organisationnel par défaut.',
      'Le modèle de communication bancaire doit rester cohérent avec le suivi réel des réservations.',
      'Le paiement sur place évite le paiement en ligne mais doit rester cohérent avec l’organisation réelle.',
    ],
    warnings: [
      'Un mauvais paramétrage ici génère ensuite des dossiers ambigus côté réservation, virement et tableau de bord admin.',
      'N’activez pas le virement sans vérifier quel IBAN sera effectivement utilisé.',
    ],
    bestPractices: [
      'Gardez un modèle de communication clair et stable pour les virements.',
      'Vérifiez si la sortie doit utiliser l’IBAN organisationnel par défaut ou un IBAN spécifique.',
      'N’activez pas un moyen de paiement que l’équipe ne pourra pas suivre correctement.',
    ],
    relatedIds: ['outing.create.payment_account', 'outing.create.capacity', 'booking.action.validate_bank_transfer'],
  },
  {
    id: 'outing.create.payment_account',
    title: 'Compte bancaire de la sortie',
    category: 'outing.create',
    scope: 'field',
    audience: 'admin',
    summary: 'Le compte IBAN saisi sur la sortie remplace le compte organisationnel par défaut pour cette sortie uniquement.',
    body: [
      'Si vous renseignez un IBAN dans ce formulaire, il devient le compte de destination des virements de cette sortie.',
      'Si vous laissez ce champ vide, le système utilise le compte bancaire par défaut de l’organisation.',
      'Ce choix a un impact direct sur la collecte des paiements et la validation manuelle côté admin.',
    ],
    systemBehavior: [
      'IBAN sortie renseigné: priorité au compte de la sortie.',
      'IBAN sortie vide: fallback automatique vers le compte organisationnel.',
    ],
    warnings: [
      'Un mauvais IBAN crée des paiements orientés vers le mauvais compte et augmente les corrections manuelles.',
      'Ne changez pas ce paramètre sans alignement avec la personne qui suit les virements.',
    ],
    bestPractices: [
      'Utilisez un IBAN spécifique seulement quand la sortie a un besoin opérationnel clair.',
      'Vérifiez le compte affiché avant publication, surtout en mode édition.',
    ],
    relatedIds: ['outing.create.payment_setup', 'booking.action.validate_bank_transfer'],
  },
  {
    id: 'outing.create.pricing',
    title: 'Configurer les tarifs',
    category: 'outing.create',
    scope: 'field',
    audience: 'admin',
    summary: 'Définit le tarif de base obligatoire, la règle enfant gratuit optionnelle et jusqu’à 3 tarifs spéciaux stables référencés dans les réservations.',
    body: [
      'Le tarif normal (normalRate) est obligatoire et doit être supérieur à 0. Il sert de référence de base pour toute réservation.',
      'La règle enfant gratuit (freeChildRule) est optionnelle. Quand elle est activée, les enfants dont l’âge est strictement inférieur à la valeur configurée (exclusive) voyagent gratuitement. Par défaut, le seuil est 7 ans (0–6 ans inclus).',
      'Vous pouvez personnaliser le libellé affiché pour l’enfant gratuit (freeChildLabel). S’il est laissé vide, il est généré automatiquement d’après l’âge configuré.',
      'Jusqu’à 3 tarifs spéciaux peuvent être ajoutés (special_1, special_2, special_3). Chaque tarif a un identifiant stable (rateId), un libellé, un prix unitaire et un statut activé/désactivé.',
      'Les rateId sont des références utilisées dans les lignes de réservation (bookingLines). Ils ne changent pas tant que le tarif existe dans la configuration.',
    ],
    systemBehavior: [
      'Désactiver un tarif spécial le masque pour les nouvelles réservations mais préserve les lignes existantes qui y font référence.',
      'Le nombre maximum de tarifs spéciaux est 3. Au-delà, le bouton d’ajout est désactivé.',
    ],
    warnings: [
      'Ne supprimez pas un tarif spécial si des réservations actives référencent son rateId : préférez le désactiver.',
      'Le tarif normal doit être supérieur à 0. Un tarif à 0 est invalidé par le formulaire.',
    ],
    bestPractices: [
      'Configurez les tarifs avant de publier la sortie : ils sont visibles immédiatement après publication.',
      'Utilisez des libellés clairs pour les tarifs spéciaux (ex. : « Résident », « Étudiant ») pour que les clients comprennent à quoi ils correspondent.',
    ],
    relatedIds: ['outing.create.overview', 'outing.create.capacity'],
  },
  {
    id: 'outing.create.capacity',
    title: 'Définir la capacité maximale',
    category: 'outing.create',
    scope: 'field',
    audience: 'admin',
    summary: 'Le nombre de places contrôle la disponibilité visible côté client et le suivi des participants.',
    body: [
      'La capacité maximale est utilisée pour limiter les réservations et afficher les places restantes.',
      'Une valeur incohérente peut provoquer des blocages commerciaux ou une surcapacité difficile à gérer.',
      'Sur une sortie déjà publiée ou avec des réservations existantes, modifier cette valeur change immédiatement l’équilibre opérationnel.',
    ],
    warnings: [
      'Ne baissez pas la capacité sous le volume déjà engagé sans vérifier les dossiers existants.',
      'Sur une sortie avec des réservations ou paiements en cours, une correction tardive peut créer des arbitrages manuels côté admin.',
    ],
    bestPractices: [
      'Définissez une capacité réaliste avant d’ouvrir la sortie à la réservation.',
      'Si la sortie est déjà diffusée, revérifiez le nombre de participants actuels avant toute modification.',
    ],
    relatedIds: ['outing.create.overview', 'outing.create.schedule'],
  },
  {
    id: 'outing.create.meeting_point',
    title: 'Configurer le point de rendez-vous',
    category: 'outing.create',
    scope: 'field',
    audience: 'admin',
    summary: 'Le lien de rendez-vous est réutilisé ensuite pour orienter les participants confirmés.',
    body: [
      'Cette URL sert de plan d’accès dans les réservations confirmées.',
      'Si elle est mauvaise ou imprécise, le problème se répercute directement le jour de la sortie.',
    ],
    bestPractices: [
      'Utilisez un lien Google Maps valide, précis et stable.',
      'Vérifiez le lien dans un nouvel onglet avant publication.',
    ],
    relatedIds: ['outing.create.overview'],
  },
  {
    id: 'outing.create.schedule',
    title: 'Configurer la date et l’heure',
    category: 'outing.create',
    scope: 'field',
    audience: 'admin',
    summary: 'La date pilote la visibilité de la sortie, la fermeture des réservations et plusieurs règles métier.',
    body: [
      'Dans cette application, une sortie trop proche ou passée peut disparaître de la liste publique ou ne plus accepter de réservation.',
      'Modifier la date d’une sortie déjà publiée change aussi les fenêtres de paiement, d’annulation et de présence.',
      "Par défaut, les réservations ferment 24 heures avant le départ. Ce délai peut être réduit en activant l'option « Réservations de dernière minute » et en saisissant une fenêtre entre 1 et 23 heures.",
    ],
    warnings: [
      "Évitez de modifier la date d'une sortie avec des réservations existantes sans prévenir les participants.",
      'Une date incorrecte peut fermer les réservations plus tôt que prévu.',
      "L'option de réservation de dernière minute écrase la règle des 24 heures : vérifiez sa valeur si la fermeture anticipée est critique pour votre organisation.",
    ],
    bestPractices: [
      'Vérifiez le fuseau horaire et l’heure réelle avant d’enregistrer.',
      'Si la sortie est déjà diffusée, confirmez l’impact sur les dossiers en cours avant édition.',
    ],
    relatedIds: ['outing.create.overview', 'outing.create.capacity'],
  },
  {
    id: 'outing.create.accessibility',
    title: 'Accessibilité et conditions d’accueil',
    category: 'outing.create',
    scope: 'field',
    audience: 'admin',
    summary: 'Les indicateurs PMR, poussette et chiens en laisse créent des attentes fortes pour les participants.',
    body: [
      'PMR: indique si le parcours et les accès sont réellement compatibles avec des besoins de mobilité réduite.',
      'Poussette: indique si le terrain, le rythme et les passages sont adaptés à une poussette.',
      'Chiens en laisse: indique si les participants peuvent venir avec un chien tenu en laisse pendant la sortie.',
    ],
    warnings: [
      'Un indicateur mal renseigné crée des incompréhensions le jour J et peut dégrader l’expérience participant.',
      'Ces choix doivent refléter les contraintes réelles du terrain, pas seulement l’intention commerciale.',
    ],
    bestPractices: [
      'Validez ces trois points avec l’équipe terrain avant publication.',
      'Mettez à jour ces indicateurs dès qu’un itinéraire ou une condition d’accueil change.',
    ],
    relatedIds: ['outing.create.overview', 'outing.create.meeting_point'],
  },
  {
    id: 'admin.outing.publication',
    title: 'Publier une sortie et envoyer l’annonce',
    category: 'admin.outing',
    scope: 'action',
    audience: 'admin',
    summary: 'La publication rend la sortie visible aux clients. L’envoi de l’annonce est une étape distincte, irréversible, qui notifie les abonnés par email et push.',
    body: [
      'Une sortie en brouillon est visible uniquement dans l’interface admin. La publier la rend accessible aux clients pour réservation.',
      'La publication est une action à sens unique : il n’est pas possible de repasser une sortie publiée en brouillon.',
      'Après publication, le bloc « Aperçu annonce » s’affiche. Il permet deux actions : Calculer l’audience (simulation sans envoi) et Envoyer l’annonce (envoi réel, irréversible).',
      'Calculer l’audience retourne le nombre estimé de destinataires email et push sans déclencher aucun envoi. Utilisez-le avant chaque annonce pour vérifier la portée.',
      'Envoyer l’annonce émet immédiatement les emails newsletter et les notifications push aux abonnés concernés. Cette action ne peut pas être annulée ni répétée une fois le statut passé à Â« completed Â» ou Â« already_sent Â».',
    ],
    systemBehavior: [
      'Statuts d’annonce : not_started (pas encore envoyée), processing (envoi en cours), completed (réussi), partial_failed (envoi partiel), failed (échec), already_sent (déjà envoyée).',
      'Après envoi, un résumé affiche les compteurs : emailSucceeded, emailFailed, pushSucceeded, pushFailed.',
      'Si le statut est already_sent, le bouton Envoyer l’annonce est désactivé.',
    ],
    warnings: [
      'L’envoi de l’annonce est immédiat et irréversible : Calculer l’audience d’abord pour vérifier les destinataires.',
      'Ne publiez pas une sortie dont les détails (date, tarifs, lieu) ne sont pas finalisés.',
    ],
    bestPractices: [
      'Toujours utiliser Calculer l’audience avant Envoyer l’annonce.',
      'Finaliser les tarifs et le lieu avant de publier : ces éléments sont visibles immédiatement après publication.',
    ],
    relatedIds: ['admin.dashboard.overview', 'outing.create.overview'],
  },
  {
    id: 'admin.dashboard.overview',
    title: 'Pilotage du tableau de bord admin',
    category: 'admin.dashboard',
    scope: 'page',
    audience: 'admin',
    summary: 'Cette zone sert à repérer rapidement les dossiers à valider, annuler ou rembourser, et à piloter le cycle de publication des sorties.',
    body: [
      'Le tableau de bord regroupe les sorties et les réservations qui demandent une action métier.',
      'Chaque sortie porte un badge de statut de publication : Brouillon ou Publié. Une sortie en brouillon n’est pas visible par les clients.',
      'Après publication, un bloc « Aperçu annonce » permet d’estimer l’audience puis d’envoyer les notifications email et push. L’envoi de l’annonce est une action distincte de la publication et ne peut pas être annulée.',
      'Le bas de cette vue permet de configurer le compte bancaire par défaut de l’organisation, utilisé en fallback pour les virements.',
      'Servez-vous des statuts et des actions disponibles pour traiter les dossiers sensibles dans le bon ordre.',
    ],
    steps: [
      'Ouvrez la sortie concernée pour voir les dossiers associés.',
      'Si la sortie est en brouillon, publiez-la pour la rendre visible côté client.',
      'Après publication, calculez l’audience de l’annonce avant d’envoyer pour estimer les destinataires email et push.',
      'Lisez d’abord les statuts de paiement, réservation et remboursement.',
      'Validez les virements reçus, puis traitez les annulations ou remboursements si nécessaire.',
    ],
    warnings: [
      'Les actions de remboursement et d’annulation ont un impact client direct.',
      'Avant toute validation, vérifiez que le statut affiché correspond bien à la situation réelle.',
      'L’envoi de l’annonce est immédiat et irréversible : utilisez d’abord « Calculer l’audience » pour vérifier les destinataires.',
    ],
    bestPractices: [
      'Utilisez les statuts comme point de décision principal.',
      'Exportez le rapport de sortie pour les contrôles financiers après traitement.',
      'Vérifiez le compte bancaire organisationnel en bas de la liste des sorties si plusieurs sorties utilisent des IBANs différents.',
    ],
    relatedIds: ['admin.outing.publication', 'booking.status.pending_validation', 'booking.action.validate_bank_transfer', 'booking.action.cancel_booking', 'booking.status.refund_pending'],
  },
  {
    id: 'booking.page.outing_detail',
    title: 'Réserver une expédition',
    category: 'booking.page',
    scope: 'page',
    audience: 'all',
    summary: 'Cette page combine les informations de sortie, la réservation et, si la sortie est complète, l’alerte de liste d’attente.',
    body: [
      'La colonne de gauche présente les détails de l’expédition, le lieu et le matériel conseillé.',
      'Le bloc de droite change d’état selon le mode de règlement choisi, l’avancement du paiement et la disponibilité réelle.',
      'Si la sortie est complète mais encore ouverte, un formulaire de liste d’attente peut être proposé à la place de la réservation.',
    ],
    systemBehavior: [
      'Un paiement Mollie peut déclencher une phase de vérification automatique au retour sur la page.',
      'Un virement bancaire crée une réservation en attente avant validation manuelle.',
      'La liste d’attente ne réserve pas automatiquement une place et sert uniquement à prévenir si de la capacité revient.',
    ],
    steps: [
      'Vérifiez les places restantes et la date de la sortie.',
      'Si une réservation est possible, renseignez vos coordonnées et le nombre de participants.',
      'Choisissez le mode de règlement adapté à votre situation.',
      'Si la sortie est complète, utilisez plutôt l’alerte de liste d’attente si elle est proposée.',
      'Suivez ensuite les instructions affichées à l’écran.',
    ],
    warnings: [
      'Ne refaites pas une seconde réservation si le paiement est déjà en cours de vérification.',
      'Les réservations sont fermées à moins de 24 heures du départ.',
      'Une inscription en liste d’attente ne garantit pas une place ni un ordre de réservation futur.',
    ],
    bestPractices: [
      'Utilisez une adresse e-mail valide pour recevoir le suivi de réservation.',
      'Conservez la communication bancaire si vous choisissez le virement.',
    ],
    relatedIds: ['booking.payment.methods', 'booking.payment.bank_transfer', 'booking.status.pending_validation', 'booking.waitlist.overview'],
  },
  {
    id: 'booking.waitlist.overview',
    title: 'Alerte liste d’attente',
    category: 'booking.waitlist',
    scope: 'page',
    audience: 'all',
    summary: 'Cette alerte sert à être prévenu si une place se libère sur une sortie complète.',
    body: [
      'Le formulaire enregistre votre intérêt pour cette sortie quand il n’y a plus de places disponibles.',
      'Vous pouvez choisir d’être prévenu par e-mail et, si votre session le permet, par notification push.',
      'Le consentement demandé ici sert uniquement aux notifications liées à cette sortie complète.',
    ],
    systemBehavior: [
      'L’alerte ne réserve pas automatiquement une place pour vous.',
      'Le système peut empêcher l’inscription si la disponibilité a déjà changé ou si la sortie n’est plus éligible.',
      'La désinscription n’est possible que si la session courante permet d’identifier votre alerte.',
    ],
    steps: [
      'Renseignez votre nom et votre e-mail.',
      'Choisissez au moins un canal de notification.',
      'Acceptez le consentement spécifique puis enregistrez l’alerte.',
    ],
    warnings: [
      'Cette alerte ne garantit pas une place.',
      'Si la disponibilité change entre-temps, rechargez la page avant de recommencer.',
    ],
    bestPractices: [
      'Gardez un canal de notification réellement consulté.',
      'Réservez directement si des places réapparaissent au lieu de recréer plusieurs alertes.',
    ],
    relatedIds: ['booking.page.outing_detail'],
  },
  {
    id: 'booking.page.my_bookings',
    title: 'Suivre mes réservations',
    category: 'booking.my_bookings',
    scope: 'page',
    audience: 'all',
    summary: 'Cette page centralise vos réservations, leurs statuts et les actions encore autorisées.',
    body: [
      'Chaque carte affiche l’état de réservation, de paiement, de présence et, si besoin, de remboursement.',
      'Les actions visibles changent selon la date de sortie, le paiement déjà reçu et le nombre de participants encore actifs.',
      'Certaines annulations déclenchent un parcours de remboursement partiel avec saisie d’IBAN.',
    ],
    systemBehavior: [
      'À moins de 24 heures avant la sortie, l’annulation standard peut disparaître et laisser place à d’autres règles métier.',
      'Une annulation partielle peut créer un remboursement distinct du remboursement total de la réservation.',
      'Le signalement d’absence est réservé à une fenêtre proche du départ.',
    ],
    steps: [
      'Repérez d’abord les badges de statut de la réservation concernée.',
      'Utilisez seulement l’action encore proposée pour cette réservation.',
      'Si un IBAN de remboursement est demandé, renseignez-le une seule fois avec soin.',
    ],
    warnings: [
      'Ne multipliez pas les annulations ou saisies d’IBAN sur la même réservation.',
      'Une réservation annulée ou expirée ne donne plus accès aux mêmes actions qu’une réservation confirmée.',
    ],
    bestPractices: [
      'Vérifiez le détail de la sortie avant d’agir sur votre réservation.',
      'Conservez vos coordonnées bancaires exactes si un remboursement manuel devient nécessaire.',
    ],
    relatedIds: ['booking.action.cancel_booking', 'booking.status.refund_pending'],
  },
  {
    id: 'booking.payment.methods',
    title: 'Choisir un mode de règlement',
    category: 'booking.payment',
    scope: 'field',
    audience: 'all',
    summary: 'Chaque mode de paiement n’entraîne pas la même confirmation de réservation.',
    body: [
      'Carte bancaire via Mollie : confirmation la plus rapide après retour de paiement.',
      'Virement bancaire : la demande est créée immédiatement, puis confirmée quand le paiement est reçu et validé.',
      'Paiement sur place : la réservation est enregistrée sans passage par un paiement en ligne.',
    ],
    bestPractices: [
      'Choisissez Mollie si vous souhaitez une confirmation rapide.',
      'Choisissez le virement seulement si vous pouvez envoyer le paiement rapidement avec la bonne communication.',
    ],
    relatedIds: ['booking.payment.bank_transfer', 'booking.status.pending_validation'],
  },
  {
    id: 'booking.payment.bank_transfer',
    title: 'Virement bancaire',
    category: 'booking.payment',
    scope: 'field',
    audience: 'all',
    summary: 'Le virement crée une réservation en attente de validation.',
    body: [
      'Quand vous choisissez le virement, Jacana enregistre la demande mais la place n’est pas encore définitivement sécurisée côté paiement.',
      'Une communication unique est générée pour rattacher votre virement à la bonne réservation.',
      'La réservation passe ensuite en confirmée après réception et validation du paiement par l’équipe.',
      'Si le virement n’est pas reçu à temps, la réservation peut rester en attente ou être annulée avant la sortie.',
    ],
    systemBehavior: [
      'Le système crée la réservation puis affiche une communication bancaire dédiée.',
      'L’admin peut valider ensuite le paiement depuis le tableau de bord.',
    ],
    warnings: [
      'Une mauvaise communication peut retarder l’identification du paiement.',
      'Ne créez pas une nouvelle réservation pour la même sortie pendant l’attente.',
    ],
    bestPractices: [
      'Copiez la communication exactement comme affichée.',
      'Effectuez le virement le plus tôt possible avant la sortie.',
    ],
    relatedIds: ['booking.status.pending_validation', 'booking.payment.methods'],
  },
  {
    id: 'booking.status.pending_validation',
    title: 'Statut en attente de validation',
    category: 'booking.status',
    scope: 'status',
    audience: 'all',
    summary: 'Pour l’admin, ce statut signifie qu’un contrôle bancaire reste requis avant confirmation.',
    body: [
      'La réservation a bien été créée, mais le règlement n’est pas encore validé comme réellement reçu.',
      'Dans ce projet, ce cas concerne surtout les virements bancaires et certains dossiers à contrôle manuel.',
      'Le client peut être en attente légitime : l’objectif côté admin est de vérifier puis solder le dossier sans doublon.',
    ],
    systemBehavior: [
      'Le dossier reste visible comme actionnable dans la liste admin.',
      'Après validation, la réservation passe en confirmée et le paiement en payé.',
    ],
    warnings: [
      'Ne validez pas un dossier uniquement parce qu’une demande ou une communication existe.',
      'Un second dossier ne doit pas être créé pour contourner ce statut sans vérification métier.',
    ],
    bestPractices: [
      'Contrôlez la communication bancaire, le montant reçu et la bonne réservation avant validation.',
    ],
    relatedIds: ['booking.payment.bank_transfer', 'booking.action.validate_bank_transfer'],
  },
  {
    id: 'booking.status.refund_pending',
    title: 'Remboursement à traiter',
    category: 'booking.status',
    scope: 'status',
    audience: 'admin',
    summary: 'Une réservation annulée ou partiellement annulée a encore un remboursement en attente d’informations, de décision ou d’exécution.',
    body: [
      'Ce cas survient lorsqu’un client avait déjà payé et que l’annulation implique un suivi financier complet ou partiel.',
      'Le dossier peut attendre un IBAN client, un traitement manuel, un refus motivé ou une confirmation de remboursement effectué.',
      'Le statut visible côté admin ne suffit pas toujours à lui seul : regardez aussi si le remboursement concerne toute la réservation ou seulement une partie des participants.',
    ],
    steps: [
      'Vérifiez que la réservation est bien annulée.',
      'Confirmez que le paiement a réellement été encaissé.',
      'Identifiez si le dossier attend encore un IBAN client ou s’il est déjà prêt pour un traitement manuel.',
      'Utilisez ensuite l’action de remboursement ou de refus selon le cas métier.',
    ],
    warnings: [
      'Ne laissez pas un dossier remboursable sans suivi trop longtemps.',
      'Ne marquez pas un remboursement comme traité tant que le virement ou le remboursement fournisseur n’est pas réellement parti.',
      'Assurez-vous que la décision prise correspond à la politique Jacana.',
    ],
    relatedIds: ['booking.action.cancel_booking', 'admin.dashboard.overview'],
  },
  {
    id: 'booking.action.validate_bank_transfer',
    title: 'Valider un virement manuel',
    category: 'booking.action',
    scope: 'action',
    audience: 'admin',
    summary: 'Cette action confirme le paiement reçu et fait avancer la réservation.',
    body: [
      'La validation confirme que le virement bancaire a bien été reçu et débloque la réservation.',
      'La fenêtre de confirmation affiche le montant attendu (total dû) pour cette réservation. Comparez ce montant au virement réellement reçu avant de valider.',
    ],
    systemBehavior: [
      'Après validation, le statut de la réservation passe de « En attente de validation » à « Confirmée ».',
      'Un email de confirmation est envoyé automatiquement au client.',
      'Si les montants (moneyTotals) sont manquants pour un dossier, la validation est bloquée automatiquement.',
    ],
    warnings: [
      'Cette action est irréversible : vérifiez le numéro de dépôt ou la référence de virement avant de confirmer.',
      'Si le montant affiché dans la fenêtre ne correspond pas au virement reçu, n’approuvez pas.',
    ],
    relatedIds: ['booking.status.pending_validation', 'admin.dashboard.overview'],
  },
  {
    id: 'booking.action.cancel_booking',
    title: 'Annuler une réservation ou une sortie entière',
    category: 'booking.action',
    scope: 'action',
    audience: 'admin',
    summary: 'Permet d’annuler une seule réservation ou toute une sortie. L’annulation en cascade clôt toutes les réservations actives et envoie automatiquement les emails aux participants.',
    body: [
      'Pour une réservation individuelle : sélectionnez le dossier et utilisez l’action Annuler. Le client reçoit un email et le remboursement est déclenché si applicable.',
      'Pour annuler toute une sortie : utilisez l’action d’annulation au niveau de la sortie. Toutes les réservations actives sont clôturées en cascade, les participants reçoivent automatiquement un email, et un message de l’organisateur (500 caractères max) peut être inclus.',
    ],
    systemBehavior: [
      'Annulation individuelle : un seul dossier passe en statut annulé, les autres réservations de la sortie ne sont pas affectées.',
      'Annulation en cascade : toutes les réservations actives de la sortie sont annulées simultanément. Les emails sont envoyés immédiatement après confirmation.',
    ],
    warnings: [
      'Une annulation validée ne peut pas être annulée.',
      'L’annulation d’une sortie entière envoie les emails aux participants immédiatement : vérifiez le message organisateur avant de confirmer.',
    ],
    relatedIds: ['booking.status.refund_pending', 'admin.dashboard.overview'],
  },
  {
    id: 'booking.action.report_absence',
    title: 'Signaler une absence',
    category: 'booking.action',
    scope: 'action',
    audience: 'all',
    summary: 'Le signalement d’absence sert à prévenir l’équipe quand vous ne pouvez plus venir sur une sortie proche.',
    body: [
      'Cette action est différente d’une annulation classique : elle apparaît surtout dans une fenêtre proche du départ.',
      'Le but est d’aider l’équipe Jacana à ajuster l’organisation du jour J.',
    ],
    steps: [
      'Vérifiez d’abord que la sortie concernée est bien la bonne.',
      'Utilisez ensuite le bouton de signalement d’absence quand il est proposé.',
    ],
    warnings: [
      'Ce signalement ne remplace pas automatiquement une annulation complète avec remboursement.',
      'Si l’action n’apparaît pas, c’est souvent que la fenêtre autorisée n’est pas ouverte ou qu’un autre statut s’applique.',
    ],
    bestPractices: [
      'Signalez votre absence dès que possible pour éviter une confusion côté organisation.',
    ],
    relatedIds: ['booking.action.cancel_participants', 'booking.page.my_bookings'],
  },
  {
    id: 'booking.action.cancel_participants',
    title: 'Annuler une partie des participants',
    category: 'booking.action',
    scope: 'action',
    audience: 'all',
    summary: 'Cette action permet de garder la réservation active tout en retirant seulement une partie des participants.',
    body: [
      'Utilisez cette option si seules certaines personnes ne viennent plus.',
      'Le système recalcule ensuite les participants actifs et peut ouvrir un remboursement partiel si nécessaire.',
    ],
    steps: [
      'Choisissez le nombre exact de participants à annuler.',
      'Ajoutez une raison si vous le souhaitez.',
      'Si un remboursement est attendu, vérifiez les coordonnées demandées avant confirmation.',
    ],
    warnings: [
      'Si vous annulez autant de participants qu’il en reste, la réservation peut devenir totalement annulée.',
      'Vérifiez bien le nombre saisi avant de confirmer.',
    ],
    bestPractices: [
      'Préférez cette action à l’annulation totale quand seule une partie du groupe change.',
    ],
    relatedIds: ['booking.action.report_absence', 'booking.partial_refund.customer_iban', 'booking.page.my_bookings'],
  },
  {
    id: 'booking.partial_refund.customer_iban',
    title: 'Renseigner mon IBAN de remboursement',
    category: 'booking.partial_refund',
    scope: 'action',
    audience: 'all',
    summary: 'Quand un remboursement partiel est prévu, Jacana peut vous demander un IBAN pour finaliser le paiement.',
    body: [
      'L’IBAN et le nom du titulaire servent uniquement au remboursement lié à cette réservation.',
      'Après envoi, le dossier passe généralement en traitement manuel côté équipe.',
    ],
    steps: [
      'Saisissez un IBAN belge valide.',
      'Renseignez le nom du titulaire du compte.',
      'Vérifiez le montant affiché puis envoyez les coordonnées.',
    ],
    warnings: [
      'Une erreur d’IBAN peut retarder le remboursement.',
      'Ne réenvoyez pas plusieurs fois des coordonnées différentes sans raison claire.',
    ],
    bestPractices: [
      'Copiez l’IBAN depuis votre banque pour éviter les fautes de saisie.',
    ],
    relatedIds: ['booking.partial_refund.status', 'booking.action.cancel_participants', 'booking.page.my_bookings'],
  },
  {
    id: 'booking.partial_refund.status',
    title: 'Comprendre le statut de remboursement partiel',
    category: 'booking.partial_refund',
    scope: 'status',
    audience: 'all',
    summary: 'Le message affiché sur votre réservation indique où en est le remboursement partiel.',
    body: [
      'IBAN requis : l’équipe attend encore vos coordonnées bancaires.',
      'En traitement : les coordonnées sont reçues et le remboursement est en cours de traitement manuel.',
      'Effectué : le remboursement a été marqué comme réalisé.',
      'Refusé : le remboursement partiel n’a pas été validé et un motif peut être affiché.',
    ],
    warnings: [
      'Un statut de remboursement partiel concerne seulement la part annulée, pas forcément toute la réservation.',
    ],
    bestPractices: [
      'Lisez d’abord ce statut avant de refaire une action sur la même réservation.',
    ],
    relatedIds: ['booking.partial_refund.customer_iban', 'booking.page.my_bookings'],
  },
  {
    id: 'booking.partial_refund.admin_processing',
    title: 'Traiter un remboursement partiel (admin)',
    category: 'booking.partial_refund',
    scope: 'action',
    audience: 'admin',
    summary: 'Ce workflow s’applique quand une réservation payée est annulée en partie ou quand un dossier partiel attend une décision manuelle.',
    body: [
      'Une annulation partielle peut créer un dossier de remboursement distinct du statut global de réservation.',
      'Le traitement admin consiste à vérifier les données du dossier, puis soit marquer le remboursement comme effectué, soit le refuser avec motif.',
      'Selon le cas, le dossier peut d’abord être bloqué en attente d’IBAN client avant de passer en traitement manuel.',
    ],
    systemBehavior: [
      'Les actions partielles mettent à jour les participants actifs, les montants de remboursement demandés et le statut partiel.',
      'Quand le remboursement partiel est finalisé, le statut partiel bascule vers un état résolu (effectué ou refusé).',
    ],
    steps: [
      'Vérifiez d’abord si le dossier est en attente d’IBAN client ou prêt pour traitement manuel.',
      'Contrôlez le bénéficiaire, l’IBAN, le montant et la communication affichés dans la fenêtre sécurisée.',
      'Confirmez ensuite le remboursement ou enregistrez un refus motivé.',
    ],
    warnings: [
      'Ne marquez jamais un remboursement comme effectué avant confirmation réelle du paiement.',
      'Un refus sans motif clair crée des retours opérationnels et du support inutile.',
    ],
    bestPractices: [
      'Traitez les dossiers partiels dans l’ordre des statuts affichés pour éviter les doublons.',
    ],
    relatedIds: ['booking.partial_refund.amounts', 'booking.partial_refund.iban_validation', 'booking.status.refund_pending', 'booking.action.cancel_booking'],
  },
  {
    id: 'booking.partial_refund.amounts',
    title: 'Interpréter les montants de remboursement partiel',
    category: 'booking.partial_refund',
    scope: 'status',
    audience: 'admin',
    summary: 'Détermine les montants à rembourser en sélectionnant, pour chaque ligne tarifaire, le nombre de participants à retirer.',
    body: [
      'La fenêtre d’annulation partielle liste les lignes tarifaires actives de la réservation (tarif normal, enfant gratuit, tarifs spéciaux).',
      'Pour chaque ligne, vous saisissez le nombre de participants à annuler. Le montant estimé est calculé automatiquement : quantité × prix unitaire de la ligne.',
      'Si la sélection retire tous les participants actifs sur toutes les lignes, la réservation bascule en annulation totale.',
    ],
    systemBehavior: [
      'Si les lignes de réservation (bookingLines) sont manquantes, la boîte de dialogue est bloquée et un message d’erreur s’affiche.',
    ],
    steps: [
      'Ouvrez le dossier de réservation et cliquez sur Annulation partielle.',
      'Pour chaque ligne tarifaire, renseignez le nombre de participants à annuler (entre 0 et la quantité active).',
      'Vérifiez le total estimé affiché avant de confirmer.',
      'Revalidez dans la boîte de dialogue de traitement si un remboursement IBAN est nécessaire.',
    ],
    warnings: [
      'Les montants calculés sont des estimations basées sur les prix unitaires : vérifiez le total avant de soumettre.',
      'Si la boîte de dialogue signale des lignes manquantes, contactez le support avant de procéder.',
    ],
    relatedIds: ['booking.partial_refund.admin_processing', 'booking.action.cancel_booking'],
  },
  {
    id: 'booking.partial_refund.iban_validation',
    title: 'Vérifier l’IBAN client avant traitement',
    category: 'booking.partial_refund',
    scope: 'field',
    audience: 'admin',
    summary: 'Les coordonnées bancaires doivent être cohérentes avant de lancer ou valider un remboursement partiel manuel.',
    body: [
      'Un IBAN belge invalide ou un titulaire incohérent bloque ou retarde le traitement du dossier.',
      'Dans les écrans d’annulation, la saisie IBAN peut être optionnelle mais devient critique dès qu’un remboursement doit partir.',
      'La fenêtre de traitement partiel affiche ensuite les coordonnées consolidées à contrôler une dernière fois.',
    ],
    steps: [
      'Vérifiez le format IBAN (BE + 14 chiffres) et la cohérence du titulaire.',
      'Confirmez que les coordonnées visibles dans la fenêtre de traitement correspondent au dossier client attendu.',
      'Ne validez le remboursement qu’après ce contrôle.',
    ],
    warnings: [
      'Un IBAN erroné peut produire un échec bancaire tout en laissant croire que le dossier est traité.',
      'Évitez les corrections partielles non tracées: appliquez une version claire et unique des coordonnées.',
    ],
    bestPractices: [
      'En cas de doute, laissez le dossier en attente d’information fiable plutôt que de forcer un traitement.',
    ],
    relatedIds: ['booking.partial_refund.admin_processing', 'booking.status.refund_pending'],
  },
  {
    id: 'legal.page.consent_settings',
    title: 'Gérer mes consentements',
    category: 'legal.consent_settings',
    scope: 'page',
    audience: 'all',
    summary: 'Cette page sépare la réacceptation légale, la newsletter marketing et la mesure d’audience optionnelle.',
    body: [
      'Les documents légaux versionnés ne servent pas au même usage que les préférences marketing ou analytics.',
      'Une réacceptation complète peut être demandée si les versions en vigueur ont changé ou si aucune preuve versionnée n’existe pour ce compte.',
      'Les choix newsletter et analytics peuvent ensuite être gérés indépendamment.',
    ],
    systemBehavior: [
      'Le bandeau global peut recommander une mise à jour avant vos prochains parcours engagés.',
      'Certaines préférences peuvent être refusées si la réacceptation légale requise n’a pas encore été faite.',
    ],
    steps: [
      'Vérifiez d’abord si une réacceptation complète est demandée.',
      'Mettez ensuite à jour séparément la newsletter et la mesure d’audience selon votre choix.',
    ],
    warnings: [
      'Modifier la newsletter ne remplace pas la réacceptation des documents légaux.',
      'Modifier l’analytics ne change pas vos consentements marketing.',
    ],
    bestPractices: [
      'Lisez chaque bloc comme une préférence distincte.',
      'Ne supposez pas qu’un choix déjà enregistré vaut pour tous les autres consentements.',
    ],
  },
  {
    id: 'admin.users.overview',
    title: 'Gérer les utilisateurs admin',
    category: 'admin.users',
    scope: 'page',
    audience: 'admin',
    summary: 'Cette page sert à créer, modifier, activer, archiver et réinitialiser les accès d’administration.',
    body: [
      'Les actions proposées ici touchent les comptes internes Jacana et doivent rester exceptionnelles et traçables.',
      'La liste combine recherche, filtres, tri et actions directes sur les comptes existants.',
      'L’archivage et la désactivation ne correspondent pas exactement au même besoin métier.',
    ],
    steps: [
      'Recherchez d’abord le bon utilisateur avant d’agir.',
      'Choisissez ensuite entre création, modification, activation, archivage ou réinitialisation du mot de passe.',
    ],
    warnings: [
      'Une mauvaise manipulation peut bloquer l’accès d’un membre de l’équipe ou réactiver un compte non souhaité.',
      'Avant d’archiver ou de désactiver, vérifiez que l’action correspond bien au besoin opérationnel.',
    ],
    bestPractices: [
      'Préférez la recherche et les filtres avant toute action directe.',
      'Réservez la création et la restauration aux cas validés par l’organisation.',
    ],
  },
];

const helpById = new Map(helpRegistry.map((article) => [article.id, article]));

export function getHelpArticleById(articleId?: string | null) {
  if (!articleId) return null;
  return helpById.get(articleId) ?? null;
}

export function getHelpArticleByCategory(category?: string | null) {
  if (!category) return null;
  return helpRegistry.find((article) => article.category === category) ?? null;
}